import { ButtonBase } from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";

import styled from "@emotion/styled";

export type LeadIssueSeverity = "error" | "warning";

interface SeverityProps {
    severity: LeadIssueSeverity;
}

/**
 * Severity is a colour decision and nothing else: one lookup, used by the icon,
 * the count pill and the focus ring, so both rows keep the same visual language.
 */
const toneOf = (theme: Theme, severity: LeadIssueSeverity) =>
    theme.palette[severity];

const withoutSeverity = {
    shouldForwardProp: (prop: string) => prop !== "severity",
};

export const IssueList = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

export const IssueRow = styled(ButtonBase, withoutSeverity)<SeverityProps>`
    display: flex;
    width: 100%;
    justify-content: flex-start;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    min-height: 40px;
    padding: ${({ theme }) => theme.spacing(0.5, 1)};
    /* Doubles as the divider from the field list on the first row. */
    border-top: 1px solid ${({ theme }) => theme.palette.divider};
    text-align: left;
    color: ${({ theme }) => theme.palette.text.primary};
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: ${({ theme }) => theme.typography.body2.fontSize};
    font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
    transition: ${({ theme }) =>
        theme.transitions.create("background-color", {
            duration: theme.transitions.duration.shortest,
        })};

    &:hover {
        background-color: ${({ theme }) => theme.palette.action.hover};
    }

    /* A plain class hook rather than an emotion component selector: those
       need @emotion/babel-plugin, which CRA's babel config does not have. */
    &:hover .lead-issue-chevron,
    &:focus-visible .lead-issue-chevron {
        transform: translateX(2px);
    }

    &:focus-visible {
        background-color: ${({ theme }) => theme.palette.action.hover};
        outline: 2px solid
            ${({ theme, severity }) => toneOf(theme, severity).main};
        outline-offset: -2px;
    }
`;

export const IssueIcon = styled("span", withoutSeverity)<SeverityProps>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, severity }) => toneOf(theme, severity).main};
`;

export const IssueLabel = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const IssueCount = styled("span", withoutSeverity)<SeverityProps>`
    margin-left: auto;
    flex-shrink: 0;
    border-radius: 999px;
    padding: ${({ theme }) => theme.spacing(0.25, 1)};
    font-size: 12px;
    font-weight: 500;
    line-height: 1.5;
    background-color: ${({ theme, severity }) =>
        alpha(toneOf(theme, severity).main, 0.16)};
    color: ${({ theme, severity }) => toneOf(theme, severity).dark};
`;

export const IssueChevron = styled.span`
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    color: ${({ theme }) => theme.palette.text.secondary};
    transition: ${({ theme }) =>
        theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest,
        })};
`;
